import { useState, useRef } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import {
  Upload,
  Download,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileSpreadsheet,
  MapPin,
  Image,
  Phone,
  Globe,
  Star,
  ArrowRight,
  RefreshCw,
  Home,
  Sparkles,
  MessageCircle,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminGoogleBulkUpload = () => {
  const { token } = useAuth();
  const fileInputRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const downloadTemplate = async (format) => {
    try {
      window.open(`${API_URL}/api/homestays/google-bulk-upload/sample?format=${format}`, '_blank');
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const filename = file.name.toLowerCase();
    if (!filename.endsWith('.csv') && !filename.endsWith('.xlsx') && !filename.endsWith('.xls')) {
      toast.error("Please upload a CSV or Excel (.xlsx) file");
      return;
    }

    setSelectedFile(file);
    setUploading(true);
    setPreviewData(null);
    setImportResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/homestays/google-bulk-upload/preview`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setPreviewData(response.data);
      toast.success(`Preview generated: ${response.data.stats.total} URLs found`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to parse file");
      setSelectedFile(null);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    setImporting(true);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axios.post(
        `${API_URL}/api/homestays/google-bulk-upload/import`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          timeout: 300000, // 5 minutes for large imports
        }
      );

      setImportResult(response.data.results);
      setPreviewData(null);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setPreviewData(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'valid':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Ready
          </span>
        );
      case 'invalid':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            Invalid
          </span>
        );
      case 'duplicate':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
            <AlertCircle className="w-3 h-3 mr-1" />
            Duplicate
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-google-bulk-upload">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-foreground mb-2 flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-blue-600" />
              Google Auto-Import
            </h1>
            <p className="text-muted-foreground">
              Just paste Google Maps URLs — we extract everything automatically!
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => downloadTemplate('csv')} data-testid="download-csv-btn">
              <Download className="w-4 h-4 mr-2" />
              CSV Template
            </Button>
            <Button variant="outline" onClick={() => downloadTemplate('xlsx')} data-testid="download-xlsx-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Excel Template
            </Button>
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
          <h3 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            What Gets Auto-Extracted from Google
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2 text-blue-800">
              <Home className="w-4 h-4 text-blue-600" />
              <span className="text-sm">Property Name</span>
            </div>
            <div className="flex items-center gap-2 text-blue-800">
              <MapPin className="w-4 h-4 text-blue-600" />
              <span className="text-sm">Full Address</span>
            </div>
            <div className="flex items-center gap-2 text-blue-800">
              <Phone className="w-4 h-4 text-blue-600" />
              <span className="text-sm">Phone Number</span>
            </div>
            <div className="flex items-center gap-2 text-blue-800">
              <Globe className="w-4 h-4 text-blue-600" />
              <span className="text-sm">Website</span>
            </div>
            <div className="flex items-center gap-2 text-blue-800">
              <Image className="w-4 h-4 text-blue-600" />
              <span className="text-sm">Up to 10 Photos</span>
            </div>
            <div className="flex items-center gap-2 text-blue-800">
              <Star className="w-4 h-4 text-blue-600" />
              <span className="text-sm">Google Rating</span>
            </div>
            <div className="flex items-center gap-2 text-blue-800">
              <MessageCircle className="w-4 h-4 text-blue-600" />
              <span className="text-sm">Google Reviews</span>
            </div>
            <div className="flex items-center gap-2 text-blue-800">
              <MapPin className="w-4 h-4 text-blue-600" />
              <span className="text-sm">Coordinates</span>
            </div>
            <div className="flex items-center gap-2 text-blue-800">
              <MapPin className="w-4 h-4 text-blue-600" />
              <span className="text-sm">Auto Location Map</span>
            </div>
          </div>
          <p className="text-xs text-blue-600 mt-4">
            💡 Tip: You only need to provide Google Maps URLs or Place IDs. Price can be added later in the admin panel.
          </p>
        </div>

        {/* Simple Instructions */}
        <div className="bg-white rounded-xl shadow-soft p-5">
          <h3 className="font-semibold text-lg mb-4">How It Works</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
              <div>
                <p className="font-medium">Collect Google URLs</p>
                <p className="text-sm text-muted-foreground">Copy Google Maps URLs of properties you want to add</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
              <div>
                <p className="font-medium">Paste in Template</p>
                <p className="text-sm text-muted-foreground">Add URLs to the CSV/Excel template (one per row)</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
              <div>
                <p className="font-medium">Upload & Import</p>
                <p className="text-sm text-muted-foreground">We fetch all data from Google automatically!</p>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Section */}
        {!previewData && !importResult && (
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h3 className="font-semibold text-lg mb-4">Upload Your Google URLs File</h3>
            
            <div className="border-2 border-dashed border-blue-300 rounded-xl p-10 text-center hover:border-blue-500 transition-colors bg-blue-50/30">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                id="google-bulk-upload"
                data-testid="google-bulk-upload-input"
              />
              <label htmlFor="google-bulk-upload" className="cursor-pointer">
                {uploading ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
                    <p className="text-blue-700 font-medium">Validating URLs...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                      <Upload className="w-8 h-8 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-lg text-foreground">
                        Upload CSV or Excel with Google URLs
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Each row should have a Google Maps URL or Place ID
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Supported: .csv, .xlsx
                    </p>
                  </div>
                )}
              </label>
            </div>
          </div>
        )}

        {/* Preview Section */}
        {previewData && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Preview - URLs to Import</h3>
                <Button variant="outline" size="sm" onClick={handleReset}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Upload Different File
                </Button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-gray-50 rounded-xl text-center">
                  <p className="text-3xl font-bold text-foreground">{previewData.stats.total}</p>
                  <p className="text-sm text-muted-foreground">Total URLs</p>
                </div>
                <div className="p-4 bg-green-50 rounded-xl text-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
                  <p className="text-3xl font-bold text-green-600">{previewData.stats.valid}</p>
                  <p className="text-sm text-green-700">Ready to Import</p>
                </div>
                <div className="p-4 bg-red-50 rounded-xl text-center">
                  <XCircle className="w-5 h-5 text-red-600 mx-auto mb-1" />
                  <p className="text-3xl font-bold text-red-600">{previewData.stats.invalid}</p>
                  <p className="text-sm text-red-700">Invalid URLs</p>
                </div>
                <div className="p-4 bg-yellow-50 rounded-xl text-center">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mx-auto mb-1" />
                  <p className="text-3xl font-bold text-yellow-600">{previewData.stats.duplicate}</p>
                  <p className="text-sm text-yellow-700">Duplicates</p>
                </div>
              </div>

              {/* Preview Table */}
              <div className="overflow-x-auto max-h-80 border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Row</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Google URL / Place ID</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Price</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Issues</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {previewData.preview.map((row, idx) => (
                      <tr key={idx} className={row.status === 'valid' ? 'bg-white' : row.status === 'duplicate' ? 'bg-yellow-50/50' : 'bg-red-50/50'}>
                        <td className="px-4 py-3 text-gray-500">{row.row_number}</td>
                        <td className="px-4 py-3">{getStatusBadge(row.status)}</td>
                        <td className="px-4 py-3 font-mono text-xs max-w-md truncate">
                          {row.google_maps_url || row.google_place_id || '-'}
                        </td>
                        <td className="px-4 py-3">
                          {row.price_per_night ? `₹${row.price_per_night}` : <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-4 py-3">
                          {row.errors.length > 0 ? (
                            <span className="text-red-600 text-xs">{row.errors[0]}</span>
                          ) : row.warnings.length > 0 ? (
                            <span className="text-yellow-600 text-xs">{row.warnings[0]}</span>
                          ) : (
                            <span className="text-green-600 text-xs">✓ Ready</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Import Button */}
            {previewData.stats.valid > 0 && (
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={handleReset}>
                  Cancel
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={importing}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="import-btn"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Extracting from Google...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Import {previewData.stats.valid} Homestays from Google
                    </>
                  )}
                </Button>
              </div>
            )}

            {importing && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-blue-800 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                  Fetching data from Google Places API for each URL. This may take a few minutes for large files...
                </p>
              </div>
            )}
          </div>
        )}

        {/* Import Results */}
        {importResult && (
          <div className="bg-white rounded-xl shadow-soft p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Import Complete
              </h3>
              <Button onClick={handleReset}>
                <Upload className="w-4 h-4 mr-2" />
                Import More
              </Button>
            </div>

            {/* Results Summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="p-4 bg-green-50 rounded-xl text-center">
                <p className="text-3xl font-bold text-green-600">{importResult.created}</p>
                <p className="text-sm text-green-700">Created</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-xl text-center">
                <p className="text-3xl font-bold text-yellow-600">{importResult.duplicates}</p>
                <p className="text-sm text-yellow-700">Duplicates</p>
              </div>
              <div className="p-4 bg-red-50 rounded-xl text-center">
                <p className="text-3xl font-bold text-red-600">{importResult.extraction_failed}</p>
                <p className="text-sm text-red-700">Extraction Failed</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl text-center">
                <p className="text-3xl font-bold text-gray-600">{importResult.skipped}</p>
                <p className="text-sm text-gray-700">Skipped</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-xl text-center">
                <p className="text-3xl font-bold text-blue-600">{importResult.total}</p>
                <p className="text-sm text-blue-700">Total Rows</p>
              </div>
            </div>

            {/* Created Homestays */}
            {importResult.created_homestays?.length > 0 && (
              <div>
                <h4 className="font-medium mb-3">Successfully Imported:</h4>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-72 overflow-y-auto">
                  {importResult.created_homestays.map((h, idx) => (
                    <div key={idx} className="p-4 bg-green-50 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Home className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{h.name}</p>
                          <div className="flex flex-wrap gap-2 mt-1 text-xs text-gray-600">
                            {h.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {h.location}
                              </span>
                            )}
                            {h.google_rating && (
                              <span className="flex items-center gap-1">
                                <Star className="w-3 h-3 text-yellow-500" />
                                {h.google_rating}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Image className="w-3 h-3" />
                              {h.images_count} photos
                            </span>
                            {h.reviews_imported > 0 && (
                              <span className="flex items-center gap-1">
                                <MessageCircle className="w-3 h-3 text-purple-500" />
                                {h.reviews_imported} reviews
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2 mt-1">
                            {h.has_phone && <Phone className="w-3 h-3 text-green-600" />}
                            {h.has_website && <Globe className="w-3 h-3 text-blue-600" />}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Errors */}
            {importResult.errors?.length > 0 && (
              <div className="p-4 bg-red-50 rounded-xl">
                <h4 className="font-medium text-red-800 mb-2">Errors ({importResult.errors.length}):</h4>
                <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
                  {importResult.errors.map((err, i) => (
                    <li key={i}>• {err}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminGoogleBulkUpload;
